/** @format */

import React, { FC, useState, useEffect, useRef, useMemo } from 'react'
import styled from 'styled-components'
import { useAsyncFn } from 'react-use'
import { getTemplateList } from 'src/api/ApiAlarmTemplate'
import { Select, Option, _$, Icon, FormItem } from '@king-design/react'
import AlarmRule, { AlarmRuleHandle } from 'src/containers/MonitorCreate/AlarmRule'
import { TablePage } from 'src/components'
import { handleParamsCase } from 'src/utils/common'
import { handleMetricUnits, LevelValueItemProps } from '../MonitorCreate/AlarmRule/constants'
import { PeriodOpts, SuppressDurationOpts } from 'src/components/SelectEventRule/constants'

interface SelectTemplateProps {
  className?: string
  productType: number
  type: 'threshold' | 'event'
  value?: number
  onChange: (data: any) => void
}

const SelectTemplate: FC<SelectTemplateProps> = ({
  className,
  productType,
  value,
  type,
  onChange
}) => {
  const rulesRef = useRef<AlarmRuleHandle>()
  const [templateID, setTemplateID] = useState<number>(value)

  useEffect(() => {
    setTemplateID(value)
  }, [value])

  const [{ value: templateList, loading }, templateList_fetch] = useAsyncFn(async () => {
    if (productType !== undefined && productType !== null) {
      const result = await getTemplateList({
        ProductType: productType,
        PageIndex: 1,
        PageSize: 9999
      })
      return result?.filter(({ EventRules, TriggerRules }) => {
        if (type === 'threshold') {
          return TriggerRules?.length
        } else if (type === 'event') {
          return EventRules?.length
        } else {
          return false
        }
      })
    } else {
      return []
    }
  }, [productType])

  useEffect(() => {
    templateList_fetch()
  }, [productType])

  const selectedTemplate = useMemo(() => {
    if (!templateList?.length || !templateID) {
      return undefined
    }

    const find = templateList.find(template => template.TemplateID === templateID)
    if (!find) {
      return undefined
    }
    const template = {
      ...find,
      TriggerRules: find?.TriggerRules?.map(item => {
        const { ItemKey, Units, TriggerValue, DisplayUnits, HierarchicalValue } = item
        const levelValue = {
          Critical: {
            Value: '',
            Units: Units,
            DisplayUnits: DisplayUnits
          },
          Error: {
            Value: TriggerValue,
            Units: Units,
            DisplayUnits: DisplayUnits
          },
          Warning: {
            Value: '',
            Units: Units,
            DisplayUnits: DisplayUnits
          }
        }
        if (HierarchicalValue) {
          Object.entries(HierarchicalValue).forEach(([key, item]) => {
            const { Value, DisplayUnits, Units } = item as LevelValueItemProps
            const trigerValue = handleMetricUnits(productType, ItemKey, Value, Units, DisplayUnits)
            levelValue[key] = {
              Value: trigerValue + '',
              Units: Units,
              DisplayUnits: DisplayUnits
            }
          })
        }
        const calcTriggerValue = handleMetricUnits(
          productType,
          ItemKey,
          TriggerValue,
          Units,
          DisplayUnits
        )
        return {
          ...item,
          levelValue,
          triggerValue: calcTriggerValue + ''
        }
      })
    }
    const templateLower = handleParamsCase(template, 'lower')
    onChange && onChange(template)
    return templateLower
  }, [templateList, templateID, productType])

  // 已选事件Table
  const selectedScheme = {
    eventNameDesc: {
      title: _$('事件名称'),
      width: 320
    },
    eventTags: {
      title: _$('维度'),
      template: ({ eventTags }) => {
        return (
          <div className="event_tags_td">
            {eventTags?.map(item => {
              const { tagName, tagValue } = item
              return (
                <p className="text_overflow" key={tagName}>
                  {tagName}={tagValue}
                </p>
              )
            }) || ''}
          </div>
        )
      }
    },
    effectBT: {
      title: _$('告警生效时间'),
      template: ({ effectBT, effectET }) => {
        return `${effectBT}-${effectET}`
      }
    },
    period: {
      title: _$('统计周期'),
      template: ({ period }) => {
        return _$(PeriodOpts[period])
      }
    },
    trigger: {
      title: _$('触发方式'),
      template: ({ period }) => {
        return period ? _$('累计触发') : _$('触发')
      }
    },
    accumulatedCount: {
      title: _$('持续周期数')
    },
    suppressDuration: {
      title: _$('告警间隔'),
      template: ({ suppressDuration }) => {
        return _$(SuppressDurationOpts[suppressDuration])
      }
    }
  }

  return (
    <div className={className}>
      <div className="select_template">
        <FormItem
          label={_$('条件触发模版')}
          value={templateID}
          rules={{ required: true }}
          messages={{
            required: _$('请选择条件触发模版')
          }}
        >
          <Select
            value={templateID}
            clearable
            loading={loading}
            onChangeValue={value => {
              setTemplateID(value)
            }}
            filterable
          >
            {templateList?.map(({ TemplateName, TemplateID }) => (
              <Option key={TemplateID} value={TemplateID}>
                {TemplateName}
              </Option>
            ))}
          </Select>
        </FormItem>
        <Icon
          className="ion-refresh cursor-pointer"
          onClick={() => {
            templateList_fetch()
          }}
        />
        <a
          onClick={() => {
            window.open(`/#!/monitor/alarmServer/template/edit`)
          }}
        >
          <Icon className="ion-plus-round"></Icon>
          {_$('新建模板')}
        </a>
        {templateID ? (
          <a
            onClick={() => {
              window.open(`/#!/monitor/alarmServer/template/edit/${templateID}`)
            }}
          >
            <Icon className="ion-edit"></Icon>
            {_$('编辑模板')}
          </a>
        ) : null}
      </div>
      {type === 'threshold' ? (
        <div className="threshold_select">
          <AlarmRule
            ref={rulesRef}
            className="alarm_item"
            readonly={true}
            productType={productType}
            showAlertGrading={true}
            alertGrading={selectedTemplate?.alertGradingEnabled}
            triggerRules={selectedTemplate?.triggerRules?.map(item => {
              const { itemKey, units, triggerValue, displayUnits } = item
              const value = handleMetricUnits(
                productType,
                itemKey,
                triggerValue,
                units,
                displayUnits
              )
              return {
                ...item,
                triggerValue: value + ''
              }
            })}
          />
        </div>
      ) : null}
      {type === 'event' ? (
        <div className="event_select">
          {selectedTemplate?.eventRules?.length ? (
            <TablePage
              tableProps={{
                scheme: selectedScheme,
                rowKe: row => row.eventName,
                checkType: 'none',
                data: selectedTemplate?.eventRules || []
              }}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

export default styled(SelectTemplate)`
  .k-form-label {
    width: 100px;
    margin-bottom: 0px;
  }
  .select_template {
    display: flex;
    align-items: center;
    .k-radio {
      margin-bottom: 0;
    }
    & > * {
      margin-right: 8px;
    }
    & > a {
      vertical-align: middle;
      .k-icon {
        color: #0091ea;
        margin-right: 4px;
        font-size: 14px;
      }
    }
  }
  .threshold_select {
    margin-top: 16px;
  }
  .event_select {
    margin-top: 16px;
    padding-bottom: 16px;
    padding-left: 100px;
  }
  .event_tags_td {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    p {
      background: rgb(230, 247, 255);
      color: rgb(0, 145, 234);
      margin: 2px;
      border-radius: 4px;
      padding: 0 8px;
      word-break: break-all;
    }
  }
`
